import React from "react";
import PropTypes from "prop-types";
import * as Mui from "@mui/material";
import { useLocation } from "react-router-dom";
import ApartmentRounded from "@mui/icons-material/ApartmentRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { designColors, designFontFamily, designShell, designTheme, designType, APP_BAR_HEIGHT } from "services/constants/designTokens";
import { requestCompanyPicker } from "services/utils/companyAccess";
import { AdministratorContactsPanel } from "components/DesignSystem/AdministratorContactsPanel";

const warmBorder = "#FAD7B5";
const warmTint = "#FFF7EF";
const warmIconBg = "#FDE7D3";
const warmIcon = "#EA7A2E";
const lockRed = "#EF4444";

// Two company "cards": one the workspace is available for (check) and one
// it is not (lock), with a leaf on the left.
const Illustration = () => (
  <svg width="300" height="150" viewBox="0 0 300 150" role="img" aria-hidden="true">
    <ellipse cx="160" cy="88" rx="130" ry="58" fill={designColors.slateBg} />
    <path d="M40 140 C30 110 38 92 58 82 C60 104 54 124 40 140Z" fill="#86C7A3" />
    <path d="M44 140 C52 116 66 106 84 104 C78 122 64 134 44 140Z" fill="#5FB283" />
    <path d="M150 64 C180 40 210 44 226 70" fill="none" stroke={designColors.slate300} strokeWidth="1.5" strokeDasharray="4 5" />
    <g>
      <rect x="96" y="22" width="68" height="76" rx="8" fill={designColors.white} stroke={designColors.slate200} />
      <rect x="114" y="36" width="32" height="50" rx="3" fill="#7DB4E8" />
      {[0, 1, 2, 3].map((row) => [0, 1].map((col) => (
        <rect key={`a${row}${col}`} x={119 + col * 13} y={42 + row * 10} width="8" height="6" rx="1" fill={designColors.white} />
      )))}
      <circle cx="160" cy="92" r="13" fill="#16A34A" />
      <path d="M154 92 l4 4 l8 -8" fill="none" stroke={designColors.white} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <g>
      <rect x="196" y="56" width="62" height="70" rx="8" fill={designColors.white} stroke={designColors.slate200} />
      <rect x="212" y="70" width="30" height="46" rx="3" fill="#A9CBEE" />
      {[0, 1, 2, 3].map((row) => [0, 1].map((col) => (
        <rect key={`b${row}${col}`} x={216 + col * 12} y={75 + row * 10} width="7" height="6" rx="1" fill={designColors.white} />
      )))}
      <circle cx="256" cy="120" r="13" fill={lockRed} />
      <rect x="250" y="119" width="12" height="9" rx="2" fill={designColors.white} />
      <path d="M252.5 119 v-3 a3.5 3.5 0 0 1 7 0 v3" fill="none" stroke={designColors.white} strokeWidth="2" />
    </g>
  </svg>
);

const OptionCard = ({ tone, icon, title, text, action }) => {
  const warm = tone === "warm";
  return (
    <Mui.Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        borderRadius: "10px",
        border: `1px solid ${warm ? warmBorder : "#A7DCD3"}`,
        backgroundColor: warm ? warmTint : "#F1FAF8",
      }}
    >
      <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
        <Mui.Box
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: warm ? warmIconBg : designColors.tealTint,
            color: warm ? warmIcon : designColors.tealDark,
          }}
        >
          {icon}
        </Mui.Box>
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate900 }}>{title}</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5, lineHeight: 1.6 }}>{text}</Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
      {action}
    </Mui.Box>
  );
};

OptionCard.propTypes = {
  tone: PropTypes.oneOf(["teal", "warm"]).isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  action: PropTypes.node.isRequired,
};

// Full-page screen shown in place of a workspace page when the selected
// company has no access to it. The user can switch company (opens the
// top-bar company picker) or open the organisation's contact details.
// My Workspace has no company picker, so it offers only the contacts.
export const WorkspaceUnavailableScreen = () => {
  const [contactsOpen, setContactsOpen] = React.useState(false);
  const showSwitchCompany = !useLocation().pathname.startsWith("/admin/myzone");
  return (
    <Mui.ThemeProvider theme={designTheme}>
      <Mui.Box
        sx={{
          ...designShell.pageSurface,
          fontFamily: designFontFamily,
          p: { xs: 2, md: 3 },
          minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Mui.Box
          component="section"
          aria-labelledby="workspace-unavailable-title"
          sx={{
            ...designShell.sectionCard,
            width: "100%",
            maxWidth: 720,
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 3,
            textAlign: "center",
          }}
        >
          <Mui.Box sx={{ display: "flex", justifyContent: "center", "& svg": { maxWidth: "100%", height: "auto" } }}>
            <Illustration />
          </Mui.Box>
          <Mui.Typography id="workspace-unavailable-title" component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: designColors.slate900, mt: 1.5 }}>
            Let&apos;s get you to the right workspace
          </Mui.Typography>
          <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700, mt: 1, mx: "auto", maxWidth: 480, lineHeight: 1.6 }}>
            {showSwitchCompany
              ? "This workspace isn't available for your selected company. You can switch to another company or contact your administrator for assistance."
              : "This workspace isn't available for you. Please contact your administrator for assistance."}
          </Mui.Typography>
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2.5, textAlign: "left" }}>
            {showSwitchCompany && (
            <OptionCard
              tone="teal"
              icon={<ApartmentRounded />}
              title="Switch company"
              text="Choose another company where this workspace is available to you."
              action={(
                <Mui.Button
                  variant="contained"
                  disableElevation
                  endIcon={<ArrowForwardRounded />}
                  onClick={requestCompanyPicker}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", backgroundColor: designColors.tealDark, "&:hover": { backgroundColor: designColors.tealDarker } }}
                >
                  Select Company
                </Mui.Button>
              )}
            />
            )}
            <OptionCard
              tone="warm"
              icon={<MailOutlineRounded />}
              title="Need assistance?"
              text="Your administrator can help you with workspace permissions."
              action={(
                <Mui.Button
                  variant="outlined"
                  onClick={() => setContactsOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", color: designColors.slate900, borderColor: designColors.borderInput, backgroundColor: designColors.white, "&:hover": { borderColor: designColors.borderInputHover, backgroundColor: designColors.white } }}
                >
                  Contact Administrator
                </Mui.Button>
              )}
            />
          </Mui.Stack>
        </Mui.Box>
      </Mui.Box>
      <AdministratorContactsPanel open={contactsOpen} onClose={() => setContactsOpen(false)} />
    </Mui.ThemeProvider>
  );
};
